
import { FiX, FiCopy, FiCheck } from "react-icons/fi";
import { useState } from "react";
import dayjs from "dayjs";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import useKagamiBridge from "@/hooks/useKagamiBridge";
import { useWebSocket } from "@/hooks/useWebSocket";
import Button from "../ui/Button";
import { useModal } from "@/contexts/ModalContext";
import ConfirmModal from "./ConfirmModal";

const stateColors = {
    scheduled: 'bg-gray-500/20 text-gray-400 border-gray-300/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
}

export default function TaskModal({ onClose, task }) {
    const [data, setData] = useState(task)
    const [copied, setCopied] = useState(false);
    const { cancelTask } = useKagamiBridge()
    const { openModal, closeModal } = useModal()

    const isActive = ['pending', 'in_progress', 'scheduled'].includes(data.state)

    useWebSocket(isActive ? ['task_update'] : [], {
        onMessage: (message) => {
            if (message.data?.taskId !== task._id) return
            setData(prev => ({ ...prev, ...message.data }))
        },
    })
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return dayjs(dateString).format('DD/MM/YYYY HH:mm');
    };

    const formatRunTime = () => {
        const startTime = dayjs(data.createdAt);
        const endTime = (data.state === 'pending' || data.state === 'in_progress') 
            ? dayjs() 
            : dayjs(data.updatedAt);
        const totalSeconds = endTime.diff(startTime, 'seconds');
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const handleCancelTask = async () => {
        const confirmed = await openModal(<ConfirmModal title="Cancel Task" message="Are you sure you want to cancel this task?" onConfirm={handleCancelTask} />)
        if(!confirmed) return
        const result = await cancelTask(data._id)
    
        setData(result)
    }

    return (
        <div className='w-full h-full max-w-[600px] flex flex-col bg-background border-l border-white/10'>
            {/* Header */}
            <div className='flex justify-between py-3 px-4 items-center border-b border-white/10'>
                <div className='flex items-center gap-3'>
                    <span className='text-white text-lg font-bold'>Task Details</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded border capitalize ${stateColors[data.state] || 'bg-gray-500/20 text-gray-400'}`}>
                        {data.state}
                    </span>
                    {data.state==='scheduled' && <Button type='danger-ghost' onClick={handleCancelTask}>Cancel</Button>}
                </div>
                <div className='cursor-pointer text-gray-400 hover:text-white transition-colors' onClick={() => onClose(null)}>
                    <FiX className="w-5 h-5" />
                </div>
            </div>

            {/* Summary */}
            {task && (
                <div className='px-4 py-3 border-b border-white/10 bg-white/[0.02]'>
                    <div className='grid grid-cols-2 gap-x-4 gap-y-2 text-xs'>
                        <div>
                            <span className='text-secondary-foreground/60'>Group:</span>
                            <span className='ml-2 text-secondary-foreground font-mono'>{data.model?.group}</span>
                        </div>
                        <div>
                            <span className='text-secondary-foreground/60'>Provider:</span>
                            <span className='ml-2 text-secondary-foreground font-mono'>{data.model?.provider}</span>
                        </div>
                        <div>
                            <span className='text-secondary-foreground/60'>{data.function ? 'Function:' : 'Model:'}</span>
                            <span className='ml-2 text-secondary-foreground font-mono'>{data.function || data.model?.name}</span>
                        </div>
                        <div>
                            <span className='text-secondary-foreground/80'>Category:</span>
                            <span className='ml-2 text-secondary-foreground'>{data.category}</span>
                        </div>
                        <div>
                            <span className='text-secondary-foreground/80'>Created:</span>
                            <span className='ml-2 text-secondary-foreground'>{dayjs(data.createdAt).format('DD/MM/YYYY HH:mm')}</span>
                        </div> 
                        {data.run_at && <div>
                            <span className='text-secondary-foreground/80'>Scheduled:</span>
                            <span className='ml-2 text-secondary-foreground font-mono'>{dayjs(data.run_at).format('DD/MM/YYYY HH:mm')}</span>
                        </div>}
                        {data.state!=='scheduled' && <div>
                            <span className='text-secondary-foreground/80'>Run Time:</span>
                            <span className='ml-2 text-secondary-foreground font-mono'>{formatRunTime()}</span>
                        </div>
                        }
                        <div>
                            <span className='text-secondary-foreground/80'>Status:</span>
                            <span className='ml-2 text-secondary-foreground'>{data.status}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* JSON Content */}
            <div className='flex-1 overflow-hidden flex flex-col'>
                <div className='flex justify-between items-center px-4 py-2 border-b border-white/10'>
                    <span className='text-gray-400 text-sm font-medium'>Raw JSON</span>
                    <button
                        onClick={handleCopy}
                        className='flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors'
                    >
                        {copied ? (
                            <>
                                <FiCheck className="w-3.5 h-3.5 text-green-400" />
                                <span className="text-green-400">Copied</span>
                            </>
                        ) : (
                            <>
                                <FiCopy className="w-3.5 h-3.5" />
                                <span>Copy</span>
                            </>
                        )}
                    </button>
                </div>
                <div className='flex-1 overflow-auto'>
                    <SyntaxHighlighter 
                        language="json" 
                        style={oneDark}
                        customStyle={{ 
                            margin: 0,
                            padding: '1rem',
                            background: 'transparent',
                            fontSize: '0.8rem',
                        }}
                        codeTagProps={{
                            style: {
                                background: 'transparent',
                            }
                        }}
                        wrapLongLines
                    >
                        {task ? JSON.stringify(task, null, 2) : 'No task data'}
                    </SyntaxHighlighter>
                </div>
            </div>
        </div>
    );
}
